/* eslint-disable no-console */
import types from '../mutation-types';
import PipelineCardAPI from '../../api/pipelineCards';
import { handlePipelineError } from '../../routes/dashboard/pipeline/helpers/pipelineErrorHandler';
import LeadScoresAPI from '../../api/leadScores';
import DealStatusAPI from '../../api/pipeline/dealStatus';
import { contaDaTela } from 'dashboard/routes/enderecos/conta';

// Cache duration for pipeline cards. Short TTLs cause the kanban board to
// re-fetch with limit:N every time the user navigates away and back,
// which can lose items from later pages. 5 minutes balances freshness
// with stability. Real-time WebSocket events update cards instantly anyway.
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Audit 2026-04-25 P0.7 — Map auxiliar para lookup O(1) por id.
// Substitui findIndex linear (custava O(n) com 2000+ items).
// Map global compartilhado entre mutations — não vai pro state reativo
// (Vue 3 não observa Maps profundamente; serve só pra acelerar lookups).
const itemsById = new Map();

const rebuildIndex = items => {
  itemsById.clear();
  items.forEach((item, idx) => itemsById.set(item.id, idx));
};

// setItemAt — cria um novo array com o item na posição idx substituído.
// Retorna o novo array; o chamador deve atribuir a _state.items.
//
// ANTES usava splice() in-place. O problema: watchers que observam a
// *referência* do array (ex: `() => props.pipelineCards` no PipelineTab)
// não disparam quando splice muta o array sem trocar a referência. Isso
// causava o bug "mudanças no Pipeline não aparecem em tempo real" —
// UPDATE_ITEM_STAGE, REPLACE_CARD, UPDATE_ITEM_DETAILS silenciosamente
// atualizavam o store mas o kanban não re-renderizava.
// Retornar um novo array garante que a referência muda e o watcher dispara.
const setItemAt = (items, idx, newItem) =>
  items.map((item, i) => (i === idx ? Object.freeze(newItem) : item));

const state = {
  isEnabled: false,
  items: [],
  meta: {
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false,
    stage_counts: {},
    next_cursor: null,
  },
  // P1.1 — paginação por stage (independente do array flat acima).
  // Estrutura: { [stageId]: { items: [], cursor: null, hasMore: false, loading: false } }
  // O array flat `items` continua existindo para retrocompatibilidade — derivado
  // do `cardsByStage` quando ambos estiverem populados.
  cardsByStage: {},
  selectedItem: null,
  loading: false,
  // Audit 2026-04-25 — flag separada para "carregar mais" (scroll-driven append).
  // O `loading` aciona o skeleton em PipelineTab; usar ele para appends destrói
  // o DOM da kanban e perde a posição de scroll do usuário.
  appending: false,
  error: null,
  config: {
    title: window.globalConfig.PIPELINE_CARD_TITLE || 'Gestor de Pedidos',
    defaultView: 'pipelineCard',
    webhookUrl: '',
    webhookSecret: '',
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
  },
  lastUpdated: null,
  lastFetchedUserId: null, // Track user to invalidate cache on user switch
  lastFetchedPipelineId: null, // Track pipeline to invalidate cache on pipeline switch
  // Audit 2026-05-07 — invalida cache em troca de account (Vai Pri / ClickHype
  // bug: cards de account 3 vazavam para sessão da account 72 durante
  // transição de rota, gerando rajadas de 404 protetivos no console).
  lastFetchedAccountId: null,
  leadScoreDistribution: {
    hot: 0,
    warm: 0,
    cold: 0,
    total: 0,
    average: 0,
  },
};

const getters = {
  isPipelineCardEnabled: _state => _state.isEnabled,
  // P0.2 — sem spread (era [..._state.items]). Evita criar array novo de
  // 2000+ elementos a cada acesso. Componentes que precisam de cópia local
  // fazem spread por conta própria.
  getPipelineCardItems: _state => _state.items,
  getMeta: _state => _state.meta,
  getCardsByStage: _state => _state.cardsByStage,
  getSelectedItem: _state => _state.selectedItem,
  isLoading: _state => _state.loading,
  getError: _state => _state.error,
  getPipelineCardConfig: _state => _state.config,
  getPipelineCardTitle: _state =>
    window.globalConfig.PIPELINE_CARD_TITLE ||
    _state.config?.title ||
    'PipelineCard',
  getSupportEmail: _state => _state.config?.supportEmail || '',
  getSupportPhone: _state => _state.config?.supportPhone || '',
  getSupportChatHours: _state => _state.config?.supportChatHours || '',
  getSupportChatEnabled: _state => _state.config?.supportChatEnabled || false,
  // P0.7 — lookup O(1) via Map auxiliar (era find() linear sobre 2000+ items).
  getPipelineCardItemQualification: _state => itemId => {
    const idx = itemsById.get(itemId);
    const item = idx !== undefined ? _state.items[idx] : null;
    if (!item) {
      return null;
    }
    return {
      id: item.id,
      qualificationChecklist: item.qualification_checklist || {},
      qualificationScore: item.qualification_score || 0,
      leadScore: item.lead_score || 0,
      leadScoreFactors: item.lead_score_factors || {},
      leadScoreCategory: item.lead_score_category || 'cold',
    };
  },
};

const actions = {
  setPipelineCardEnabled({ commit }, isEnabled) {
    commit(types.SET_PIPELINE_CARD_ENABLED, isEnabled);
  },

  setSelectedItem({ commit }, item) {
    commit('SET_SELECTED_ITEM', item);
  },

  async updateItemOwner({ commit }, { itemId, ownerId }) {
    try {
      const response = await PipelineCardAPI.updateCard(itemId, {
        owner_id: ownerId,
      });
      commit('UPDATE_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED');
      return response.data;
    } catch (error) {
      console.error('Failed to update item owner:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async fetchPipelineCardItems(
    { commit, state: localState },
    { append = false } = {}
  ) {
    // Get current user and pipeline to validate cache
    const currentUserId = this.getters.getCurrentUserID;
    const currentPipeline = this.getters['pipelines/getSelectedPipeline'];
    const currentPipelineId = currentPipeline?.id;
    // Audit 2026-05-07 — accountId vem da URL (rota), nunca de um getter
    // que possa apontar pra valor stale durante transição.
    // Endereço antigo ou limpo (06/10/2026) — `routes/enderecos/conta.js`.
    const currentAccountId = (() => {
      const parsed = Number(contaDaTela());
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    })();

    // Skip cache check when appending (loading more)
    if (!append) {
      // Verifica se o cache é válido (including user, pipeline, account check)
      // Convert IDs to strings for consistent comparison
      const currentPipelineIdStr = currentPipelineId
        ? String(currentPipelineId)
        : null;
      const lastFetchedPipelineIdStr = localState.lastFetchedPipelineId
        ? String(localState.lastFetchedPipelineId)
        : null;

      const now = new Date().getTime();
      const isCacheValid =
        localState.items.length > 0 &&
        localState.lastUpdated &&
        localState.lastFetchedUserId === currentUserId && // Invalidate on user switch
        lastFetchedPipelineIdStr === currentPipelineIdStr && // Invalidate on pipeline switch
        localState.lastFetchedAccountId === currentAccountId && // Invalidate on account switch
        now - localState.lastUpdated < CACHE_DURATION_MS;

      if (isCacheValid) {
        // Retorna os dados cacheados sem mudar o estado de loading
        return localState.items;
      }
    }

    // Guard against duplicate concurrent calls
    if (localState.loading || localState.appending) return localState.items;

    // Audit 2026-04-25 — append usa flag separada para não acionar o skeleton
    // (que destrói o DOM da kanban e perde scroll do usuário).
    // Recarga QUIETA (29/10/2026): o mesmo funil já na tela — cache vencido,
    // "Atualizar", fim de um filtro — troca os dados no lugar. O esqueleto
    // apagava o quadro inteiro e nenhum movimento sobrevivia a isso (roteiro
    // de movimento, armadilha 9). Primeira carga e troca de funil, pessoa ou
    // conta continuam com esqueleto: lá a tela não tem o que mostrar.
    const recargaQuieta =
      !append &&
      localState.items.length > 0 &&
      localState.lastFetchedUserId === currentUserId &&
      String(localState.lastFetchedPipelineId) === String(currentPipelineId) &&
      localState.lastFetchedAccountId === currentAccountId;
    if (append) {
      commit('SET_APPENDING', true);
    } else if (recargaQuieta) {
      commit('SET_APPENDING', true);
    } else {
      commit('SET_LOADING', true);
    }
    try {
      if (!currentPipelineId) {
        throw new Error('Nenhum pipeline selecionado');
      }

      const offset = append ? localState.items.length : 0;
      // Audit 2026-04-25 — backend limita a 500 por request (era 2000).
      // O fluxo de "load more" via offset continua funcionando: PipelineColumn
      // dispara `loadMoreFromAPI` quando faltam cards e há has_more.
      // Para stages com > 500 cards, use `fetchStageCards` (cursor pagination
      // por coluna) que é 28× mais rápido no DB e evita carregar dados que
      // o usuário não vê.
      const response = await PipelineCardAPI.getCards(currentPipelineId, {
        limit: 500,
        offset,
      });

      // Handle paginated response (data + meta)
      const responseData = response.data;
      const items = responseData.data || responseData;
      const meta = responseData.meta || null;

      if (append && meta) {
        commit('APPEND_PIPELINE_CARD_ITEMS', { data: items, meta });
      } else {
        commit('SET_PIPELINE_CARD_ITEMS', items);
        if (meta) {
          commit('SET_META', meta);
        }
      }
      commit('SET_LAST_UPDATED');
      commit('SET_LAST_FETCHED_USER', currentUserId); // Track which user fetched
      commit('SET_LAST_FETCHED_PIPELINE', currentPipelineId); // Track which pipeline fetched
      commit('SET_LAST_FETCHED_ACCOUNT', currentAccountId); // Track which account fetched
      return items;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em fetchPipelineCardItems:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      if (append || recargaQuieta) {
        commit('SET_APPENDING', false);
      } else {
        commit('SET_LOADING', false);
      }
    }
  },

  // Criar e mudar a estrutura não ligam mais o `loading` (29/10/2026): ele é o
  // esqueleto do QUADRO inteiro, e criar um cartão apagava todas as colunas
  // para devolvê-las meio segundo depois. Quem espera é o botão do formulário.
  async createPipelineCardItem({ commit }, payload) {
    try {
      const response = await PipelineCardAPI.createCard(payload);
      commit('ADD_PIPELINE_CARD_ITEM', response.data);
      commit('SET_LAST_UPDATED'); // Invalida cache ao criar
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  // Atualiza apenas o store local sem fazer chamada de API
  // Usado quando a API já foi chamada (ex: mudança de status)
  updateCardStageLocal({ commit }, { itemId, columnId }) {
    const numericItemId = parseInt(itemId, 10);
    commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
  },

  // Replace entire card in store (used when card is updated via API)
  replaceCard({ commit }, card) {
    commit('REPLACE_CARD', card);
  },

  async moveItemToStage(
    { commit, dispatch, state: localState },
    { itemId, columnId, lostReason = null, wonValue = null, wonNote = null }
  ) {
    // Garantir que itemId seja número
    const numericItemId = parseInt(itemId, 10);

    // Obter o item original para possível reversão — O(1) via Map
    const originalIdx = itemsById.get(numericItemId);
    const originalItem =
      originalIdx !== undefined ? localState.items[originalIdx] : undefined;

    if (originalItem) {
      // Modificar o item no store antes da API (igual à exclusão)
      commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
    }

    try {
      // Chamar a API passando fromStage para evitar fetch redundante
      const fromStage = originalItem?.pipeline_stage;
      const response = await PipelineCardAPI.moveToStage(
        numericItemId,
        columnId,
        fromStage,
        {
          lostReason,
          wonValue,
          wonNote,
        }
      );
      // Atualizar o card completo no store com a resposta da API
      // moveToStage retorna o card diretamente (não o wrapper axios)
      if (response) {
        commit('REPLACE_CARD', response);
      }
      // Invalidar cache do frontend após sucesso
      await dispatch('itemUpdated');
      return true;
    } catch (error) {
      // Em caso de erro, reverter para o estado original
      if (originalItem) {
        commit('UPDATE_ITEM_STAGE', {
          itemId: numericItemId,
          columnId: originalItem.pipeline_stage,
        });
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async deletePipelineCardItem({ commit, state: localState }, itemId) {
    const originalItem = localState.items.find(i => i.id === itemId);
    commit('REMOVE_PIPELINE_CARD_ITEM', itemId);

    try {
      // Pass originalItem to avoid extra getCard call in API
      await PipelineCardAPI.deleteCard(itemId, { currentCard: originalItem });
      commit('SET_LAST_UPDATED'); // Invalida cache ao deletar
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_PIPELINE_CARD_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    try {
      const response = await PipelineCardAPI.updateCard(itemId, {
        item_details: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_details?.item_structure,
      });
      await dispatch('itemUpdated'); // Invalida cache
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async updateItemDetails({ commit, state: localState }, { itemId, details }) {
    // Get current item from store (avoid extra API call)
    const currentItem = localState.items.find(i => i.id === itemId);

    // Optimistic update: update store first for faster UI
    commit('UPDATE_ITEM_DETAILS', { itemId, details });

    try {
      // Build payload using store data instead of fetching from API
      const payload = {
        pipeline_id: details.pipeline_id ?? currentItem?.pipeline_id,
        pipeline_stage: details.pipeline_stage ?? currentItem?.pipeline_stage,
        position: details.position ?? currentItem?.position,
        owner_id: details.owner_id ?? currentItem?.owner_id,
        item_details: details.item_details ?? currentItem?.item_details,
        conversation_display_id:
          details.conversation_display_id ??
          currentItem?.conversation_display_id,
        timer_started_at:
          details.timer_started_at ?? currentItem?.timer_started_at,
        timer_duration: details.timer_duration ?? currentItem?.timer_duration,
        custom_attributes:
          details.custom_attributes ?? currentItem?.custom_attributes,
      };

      // Pass currentItem to API for webhook change tracking
      const response = await PipelineCardAPI.updateCard(itemId, payload, {
        currentCard: currentItem,
      });
      return response.data;
    } catch (error) {
      // Revert optimistic update on error
      if (currentItem) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: currentItem });
      }
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async registerActivity(
    { commit, dispatch, state: localState },
    { itemId, type, details }
  ) {
    try {
      // Get current item from store instead of API call
      const currentItem = localState.items.find(i => i.id === itemId);
      const currentItemDetails = currentItem?.item_details || {};

      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: {
          id: this.getters.getCurrentUser.id,
          name: this.getters.getCurrentUser.name,
          avatar_url: this.getters.getCurrentUser.avatar_url,
        },
      };

      const activities = [
        ...(currentItemDetails.activities || []),
        newActivity,
      ];

      const payload = {
        item_details: {
          ...currentItemDetails,
          activities,
        },
      };

      const response = await PipelineCardAPI.updateCard(itemId, payload, {
        currentCard: currentItem,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async fetchPipelineCardConfig({ commit }) {
    // Config is loaded from globalConfig - no API call needed
    const config = {
      title: window.globalConfig.PIPELINE_CARD_TITLE || 'Pipeline',
      defaultView: 'pipelineCard',
      webhookUrl: '',
      webhookSecret: '',
      autoAssignment: false,
      notificationsEnabled: false,
      supportEmail: '',
      supportPhone: '',
      supportChatHours: '',
      supportChatEnabled: false,
    };

    commit('SET_PIPELINE_CARD_CONFIG', config);
    return config;
  },

  updateLocalItems({ commit }, items) {
    commit('SET_PIPELINE_CARD_ITEMS', items);
    commit('SET_LAST_UPDATED'); // Atualiza o timestamp ao receber dados externos
  },

  async recalculateLeadScore({ commit }, itemId) {
    try {
      const response = await LeadScoresAPI.recalculate(itemId);
      const item = response.data;
      const camelItem = {
        id: itemId,
        lead_score: item.lead_score,
        lead_score_factors: item.lead_score_factors,
        lead_score_category: item.lead_score_category,
        updated_at: item.updated_at,
      };
      commit('UPDATE_PIPELINE_CARD_ITEM', camelItem);
      return camelItem;
    } catch (error) {
      console.error('Failed to recalculate lead score:', error);
      throw error;
    }
  },

  async overrideLeadScore({ commit }, { itemId, score }) {
    try {
      const response = await LeadScoresAPI.override(itemId, score);
      const item = response.data;
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lead_score: item.lead_score,
        manual_override: item.manual_override,
      });
      return item;
    } catch (error) {
      console.error('Failed to override lead score:', error);
      throw error;
    }
  },

  async fetchLeadScoreDistribution({ commit }) {
    try {
      const response = await LeadScoresAPI.getDistribution();
      commit('SET_LEAD_SCORE_DISTRIBUTION', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch lead score distribution:', error);
      throw error;
    }
  },

  async markAsWon({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.markAsWon(itemId);
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: response.data.won_at,
        lost_at: null,
        lost_reason: null,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as won:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async markAsLost({ commit, dispatch }, { itemId, reason }) {
    try {
      const response = await DealStatusAPI.markAsLost(itemId, reason);
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        lost_at: response.data.lost_at,
        lost_reason: response.data.lost_reason,
        won_at: null,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to mark deal as lost:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async reopenDeal({ commit, dispatch }, itemId) {
    try {
      const response = await DealStatusAPI.reopen(itemId);
      commit('UPDATE_PIPELINE_CARD_ITEM', {
        id: itemId,
        won_at: null,
        lost_at: null,
        lost_reason: null,
      });
      await dispatch('itemUpdated');
      return response.data;
    } catch (error) {
      console.error('Failed to reopen deal:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async itemUpdated({ commit }) {
    commit('SET_LAST_UPDATED');
    return true;
  },

  // Reset cache on logout or user switch
  resetCache({ commit }) {
    commit('SET_PIPELINE_CARD_ITEMS', []);
    commit('SET_META', {
      total: 0,
      limit: 50,
      offset: 0,
      has_more: false,
      stage_counts: {},
      next_cursor: null,
    });
    commit('CLEAR_STAGE_CARDS', null);
    commit('SET_LAST_UPDATED', null);
    commit('SET_LAST_FETCHED_USER', null);
    commit('SET_LAST_FETCHED_PIPELINE', null);
    commit('SET_LAST_FETCHED_ACCOUNT', null);
  },

  // Audit 2026-05-07 — wipe full state when user switches between accounts.
  // Called by App.vue's watcher on `currentAccountId` change. Without this,
  // cards loaded for account A linger in `state.items` and components
  // mounted briefly during route transition fire requests with cardA IDs
  // against the new account's URL → backend returns 404 (protective) but
  // the console gets noisy.
  resetForAccountSwitch({ commit }) {
    commit('RESET_FOR_ACCOUNT_SWITCH');
  },

  // P1.1 — fetch cards de UM stage específico com cursor pagination.
  // Usado pela paginação por coluna do kanban. Cada coluna chama isso
  // independentemente; backend retorna no máximo `limit` cards por chamada
  // (default 50) com `next_cursor` para a próxima página.
  async fetchStageCards(
    { commit, state: localState },
    { pipelineId, stageId, append = false, limit = 50 } = {}
  ) {
    if (!pipelineId || !stageId) {
      throw new Error('pipelineId e stageId são obrigatórios');
    }
    commit('SET_STAGE_LOADING', { stageId, loading: true });

    try {
      const stageState = localState.cardsByStage[stageId];
      const cursor = append && stageState ? stageState.cursor : null;
      const params = {
        pipeline_id: pipelineId,
        pipeline_stage: stageId,
        limit,
      };
      if (cursor) params.cursor = cursor;

      const response = await PipelineCardAPI.getCards(pipelineId, params);
      const responseData = response.data;
      const items = responseData.data || responseData;
      const meta = responseData.meta || {};

      const payload = {
        stageId,
        items,
        cursor: meta.next_cursor,
        hasMore: !!meta.has_more,
      };
      if (append) {
        commit('APPEND_STAGE_CARDS', payload);
      } else {
        commit('SET_STAGE_CARDS', payload);
      }

      // Manter stage_counts atualizado se o backend retornou
      if (meta.stage_counts) {
        commit('SET_META', {
          ...localState.meta,
          stage_counts: meta.stage_counts,
        });
      }
      return items;
    } catch (error) {
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    } finally {
      commit('SET_STAGE_LOADING', { stageId, loading: false });
    }
  },

  async updateQualificationChecklist({ commit }, { itemId, checklist }) {
    try {
      const response = await PipelineCardAPI.updateQualificationChecklist(
        itemId,
        checklist
      );
      const item = response.data;
      commit('UPDATE_QUALIFICATION_CHECKLIST', {
        itemId,
        checklist: item.qualification_checklist,
        qualificationScore: item.qualification_score,
        leadScore: item.lead_score,
        leadScoreFactors: item.lead_score_factors,
      });
      commit('SET_LAST_UPDATED');
      return item;
    } catch (error) {
      console.error('Failed to update qualification checklist:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },

  async recalculateLeadScoreFromChecklist({ commit }, itemId) {
    try {
      const response = await PipelineCardAPI.recalculateScore(itemId);
      const item = response.data;
      commit('UPDATE_LEAD_SCORE', {
        itemId,
        leadScore: item.lead_score,
        leadScoreFactors: item.lead_score_factors,
        leadScoreCategory: item.lead_score_category,
      });
      commit('SET_LAST_UPDATED');
      return item;
    } catch (error) {
      console.error('Failed to recalculate lead score:', error);
      commit('SET_ERROR', error.message);
      handlePipelineError(error);
      throw error;
    }
  },
};

const mutations = {
  [types.SET_PIPELINE_CARD_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_APPENDING(_state, appending) {
    _state.appending = appending;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },
  SET_SELECTED_ITEM(_state, item) {
    _state.selectedItem = item;
  },
  // P0.2 — Object.freeze nos items individuais. Vue 3 detecta frozen e
  // pula a instalação de proxies reativos profundos. Para 2000+ cards com
  // item_details aninhado, isso elimina ~50-200ms de overhead na carga
  // inicial e em cada update reativo.
  // P0.7 — rebuilda o índice itemsById para lookup O(1) nas mutations.
  SET_PIPELINE_CARD_ITEMS(_state, items) {
    const frozen = items.map(item => Object.freeze(item));
    _state.items = frozen;
    rebuildIndex(frozen);
  },
  SET_META(_state, meta) {
    _state.meta = meta;
  },
  APPEND_PIPELINE_CARD_ITEMS(_state, { data, meta }) {
    const newFrozen = data
      .filter(item => !itemsById.has(item.id))
      .map(item => Object.freeze(item));
    if (newFrozen.length > 0) {
      const baseLength = _state.items.length;
      _state.items = [..._state.items, ...newFrozen];
      newFrozen.forEach((item, i) => itemsById.set(item.id, baseLength + i));
    }
    _state.meta = meta;
  },
  ADD_PIPELINE_CARD_ITEM(_state, item) {
    const frozen = Object.freeze(item);
    _state.items = [..._state.items, frozen];
    itemsById.set(frozen.id, _state.items.length - 1);
  },
  REMOVE_PIPELINE_CARD_ITEM(_state, itemId) {
    _state.items = _state.items.filter(item => item.id !== itemId);
    rebuildIndex(_state.items);
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    const numericItemId = parseInt(itemId, 10);
    const itemIndex = itemsById.get(numericItemId);

    if (itemIndex !== undefined && itemIndex !== -1) {
      const item = _state.items[itemIndex];
      _state.items = setItemAt(_state.items, itemIndex, {
        ...item,
        pipeline_stage: columnId,
      });
    }
  },
  REPLACE_CARD(_state, card) {
    const itemIndex = itemsById.get(card.id);
    if (itemIndex !== undefined && itemIndex !== -1) {
      _state.items = setItemAt(_state.items, itemIndex, card);
    }
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        item_structure: structure,
      });
    }
  },
  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const item = _state.items[index];
      const updatedFields = {};
      Object.keys(details).forEach(key => {
        if (key in item) {
          updatedFields[key] = details[key];
        } else if (key === 'item_details') {
          updatedFields.item_details = {
            ...item.item_details,
            ...details.item_details,
          };
        }
      });
      _state.items = setItemAt(_state.items, index, {
        ...item,
        ...updatedFields,
      });
    }
  },
  SET_PIPELINE_CARD_CONFIG(_state, config) {
    _state.config = config;
  },
  SET_LAST_UPDATED(_state) {
    _state.lastUpdated = new Date().getTime();
  },

  INVALIDATE_CACHE(_state) {
    _state.lastUpdated = null;
  },

  SET_LAST_FETCHED_USER(_state, userId) {
    _state.lastFetchedUserId = userId;
  },

  SET_LAST_FETCHED_PIPELINE(_state, pipelineId) {
    _state.lastFetchedPipelineId = pipelineId;
  },

  SET_LAST_FETCHED_ACCOUNT(_state, accountId) {
    _state.lastFetchedAccountId = accountId;
  },

  RESET_FOR_ACCOUNT_SWITCH(_state) {
    _state.items = [];
    _state.cardsByStage = {};
    _state.selectedItem = null;
    _state.meta = {
      total: 0,
      limit: 50,
      offset: 0,
      has_more: false,
      stage_counts: {},
      next_cursor: null,
    };
    _state.lastUpdated = null;
    _state.lastFetchedUserId = null;
    _state.lastFetchedPipelineId = null;
    _state.lastFetchedAccountId = null;
    _state.error = null;
    itemsById.clear();
  },

  UPDATE_PIPELINE_CARD_ITEM(_state, item) {
    const index = itemsById.get(item.id);
    if (index !== undefined && index !== -1) {
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...item,
      });
    }
  },

  SET_LEAD_SCORE_DISTRIBUTION(_state, distribution) {
    _state.leadScoreDistribution = distribution;
  },

  UPDATE_QUALIFICATION_CHECKLIST(
    _state,
    { itemId, checklist, qualificationScore, leadScore, leadScoreFactors }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = {
        qualification_checklist: checklist,
        qualification_score: qualificationScore,
      };
      if (leadScore !== undefined) {
        updatedFields.lead_score = leadScore;
      }
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  UPDATE_LEAD_SCORE(
    _state,
    { itemId, leadScore, leadScoreFactors, leadScoreCategory }
  ) {
    const index = itemsById.get(itemId);
    if (index !== undefined && index !== -1) {
      const updatedFields = { lead_score: leadScore };
      if (leadScoreFactors !== undefined) {
        updatedFields.lead_score_factors = leadScoreFactors;
      }
      if (leadScoreCategory !== undefined) {
        updatedFields.lead_score_category = leadScoreCategory;
      }
      _state.items = setItemAt(_state.items, index, {
        ..._state.items[index],
        ...updatedFields,
      });
    }
  },

  // === REAL-TIME MUTATIONS (from WebSocket) ===

  // Add card from remote (other user created)
  ADD_PIPELINE_CARD_REMOTE(_state, card) {
    // Check if card already exists (dedupe)
    if (!itemsById.has(card.id) && card.id) {
      // Convert snake_case from server to match local format
      const normalizedCard = {
        id: card.id,
        pipeline_stage: card.stage || card.pipeline_stage,
        position: card.position,
        owner_id: card.owner_id,
        conversation_display_id: card.conversation_display_id,
        title: card.title,
        updated_at: card.updated_at,
        won_at: card.won_at,
        lost_at: card.lost_at,
        lead_score: card.lead_score,
        // Preserve any additional fields that might come from full card data
        ...card,
      };
      const frozen = Object.freeze(normalizedCard);
      _state.items = [..._state.items, frozen];
      itemsById.set(frozen.id, _state.items.length - 1);
    }
  },

  // Update card from remote (other user updated)
  UPDATE_PIPELINE_CARD_REMOTE(_state, { card }) {
    const index = itemsById.get(card.id);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // updated_at can arrive in TWO formats:
      //   - REST API (initial fetch): ISO 8601 string "2026-05-07T00:31:00.000Z"
      //   - WebSocket broadcast (BroadcastService): epoch SECONDS (number)
      // Coerce both to epoch ms for safe comparison. A naive `>=` between
      // a number and a string returns false, which silently dropped every
      // realtime update on the kanban board.
      const toEpochMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toEpochMs(existing.updated_at);
      const incomingVersion = toEpochMs(card.updated_at);

      // Only update if incoming version is newer or equal (to handle concurrent updates)
      if (incomingVersion >= existingVersion) {
        // Normalize and merge — preserve local fields when broadcast payload is partial
        const normalizedCard = {
          ...existing,
          pipeline_stage:
            card.stage || card.pipeline_stage || existing.pipeline_stage,
          position: card.position ?? existing.position,
          owner_id: card.owner_id ?? existing.owner_id,
          title: card.title ?? existing.title,
          updated_at: incomingVersion,
          won_at: card.won_at !== undefined ? card.won_at : existing.won_at,
          lost_at: card.lost_at !== undefined ? card.lost_at : existing.lost_at,
          lost_reason: card.lost_reason ?? existing.lost_reason,
          lead_score: card.lead_score ?? existing.lead_score,
          deadline: card.deadline ?? existing.deadline,
          scheduled_at: card.scheduled_at ?? existing.scheduled_at,
          status: card.status ?? existing.status,
          priority: card.priority ?? existing.priority,
        };
        _state.items = setItemAt(_state.items, index, normalizedCard);
      }
    }
  },

  // Remove card from remote (other user deleted)
  REMOVE_PIPELINE_CARD_REMOTE(_state, cardId) {
    _state.items = _state.items.filter(item => item.id !== cardId);
    rebuildIndex(_state.items);
  },

  // Update card stage from remote (other user moved card)
  UPDATE_CARD_STAGE_REMOTE(_state, { cardId, stage, position, version }) {
    const index = itemsById.get(cardId);
    if (index !== undefined && index !== -1) {
      const existing = _state.items[index];
      // Same dual-format problem as UPDATE_PIPELINE_CARD_REMOTE — coerce both.
      const toMs = v => {
        if (v == null) return 0;
        if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
        const ms = Date.parse(v);
        return Number.isNaN(ms) ? 0 : ms;
      };
      const existingVersion = toMs(existing.updated_at);
      const versionMs = toMs(version);

      // Only update if incoming version is newer
      if (versionMs >= existingVersion) {
        _state.items = setItemAt(_state.items, index, {
          ...existing,
          pipeline_stage: stage,
          position: position ?? existing.position,
          updated_at: version,
        });
      }
    }
  },

  // Reorder cards from remote (other user reordered)
  REORDER_CARDS_REMOTE(_state, { positions, version }) {
    if (!positions || !Array.isArray(positions)) return;

    // Acumular todas as trocas antes de reatribuir para evitar múltiplos
    // re-renders. Usar reduce para ir construindo o array novo a cada posição.
    let updatedItems = _state.items;
    positions.forEach(pos => {
      const index = itemsById.get(pos.id);
      if (index !== undefined && index !== -1) {
        const existing = updatedItems[index];
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          position: pos.position,
          pipeline_stage:
            pos.pipeline_stage || pos.stage || existing.pipeline_stage,
          updated_at: version || existing.updated_at,
        });
      }
    });
    _state.items = updatedItems;
  },

  // Update labels for cards linked to a conversation
  UPDATE_CARD_LABELS_BY_CONVERSATION(
    _state,
    { conversationDisplayId, labels }
  ) {
    let changed = false;
    let updatedItems = _state.items;
    _state.items.forEach((item, index) => {
      if (item.conversation_display_id === conversationDisplayId) {
        const existing = updatedItems[index];
        // Update label_list in item_details.conversation
        const updatedItemDetails = {
          ...existing.item_details,
          conversation: {
            ...(existing.item_details?.conversation || {}),
            label_list: labels,
          },
        };
        updatedItems = setItemAt(updatedItems, index, {
          ...existing,
          item_details: updatedItemDetails,
        });
        changed = true;
      }
    });
    if (changed) {
      _state.items = updatedItems;
    }
  },

  // === P1.1 — CURSOR PAGINATION BY STAGE ===
  // Estado per-stage. Permite que cada coluna do kanban faça fetch
  // independente, com cursor keyset (28× mais rápido que offset no
  // worst-case 2.442-card stage).
  SET_STAGE_CARDS(_state, { stageId, items, cursor, hasMore }) {
    const frozen = items.map(item => Object.freeze(item));
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: {
        items: frozen,
        cursor: cursor || null,
        hasMore: hasMore || false,
        loading: false,
      },
    };
  },
  APPEND_STAGE_CARDS(_state, { stageId, items, cursor, hasMore }) {
    const existing = _state.cardsByStage[stageId] || {
      items: [],
      cursor: null,
      hasMore: false,
    };
    const existingIds = new Set(existing.items.map(i => i.id));
    const newFrozen = items
      .filter(i => !existingIds.has(i.id))
      .map(item => Object.freeze(item));
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: {
        items: [...existing.items, ...newFrozen],
        cursor: cursor || null,
        hasMore: hasMore || false,
        loading: false,
      },
    };
  },
  SET_STAGE_LOADING(_state, { stageId, loading }) {
    const existing = _state.cardsByStage[stageId] || {
      items: [],
      cursor: null,
      hasMore: false,
    };
    _state.cardsByStage = {
      ..._state.cardsByStage,
      [stageId]: { ...existing, loading },
    };
  },
  CLEAR_STAGE_CARDS(_state, stageId) {
    if (stageId) {
      const next = { ..._state.cardsByStage };
      delete next[stageId];
      _state.cardsByStage = next;
    } else {
      _state.cardsByStage = {};
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
